import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  type FieldValues,
  getComponentSubtree,
} from 'features/libra/ontology/ontologyFieldTypes'
import { getEffectiveZoneArea } from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import {
  getEffectiveZoneHeight,
  getEffectiveZoneLength,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  COVERAGE_COLUMNS,
  DAMAGE_MECHANISM_COLUMNS,
  OVERVIEW_COL_TO_ZONE_FIELD,
  RBA_KEY_LIST,
  ZONE_DETAIL_FIELDS,
  ZONE_SECTION_KEYS,
  ZONE_SYNTHETIC_PREFIX,
} from 'features/libra/stepComponents/zoneAnalysis/zoneDetailConstants'

import type { CommentAnchor } from './commentTypes'

export interface AnchorValueContext {
  fieldValues: FieldValues
  zones?: ZoneParams[]
}

export type AnchorValueResolver = (
  anchor: CommentAnchor,
  context: AnchorValueContext
) => unknown | undefined

/**
 * Navigate a dot-separated path on an object, e.g.
 * getByPath(zone, 'nii_strategy.degradation_likely')
 */
function getByPath(obj: Record<string, unknown>, path: string): unknown {
  let current: unknown = obj
  for (const segment of path.split('.')) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== 'object'
    )
      return undefined
    current = (current as Record<string, unknown>)[segment]
  }
  return current
}

// ── Detail label resolver (built from shared constants) ──────────────

const COMPUTED_RESOLVERS: Record<
  string,
  (zone: ZoneParams) => unknown | undefined
> = {
  effectiveLength: (z) => getEffectiveZoneLength(z),
  effectiveHeight: (z) => getEffectiveZoneHeight(z),
  effectiveArea: (z) => {
    const a = getEffectiveZoneArea(z)
    return a > 0 ? a : undefined
  },
}

function resolveDetailLabel(
  zone: ZoneParams,
  label: string
): unknown | undefined {
  const entry = ZONE_DETAIL_FIELDS.find((d) => d.label === label)
  if (!entry) return undefined
  if ('resolve' in entry) {
    return COMPUTED_RESOLVERS[entry.resolve]?.(zone)
  }
  return (zone as unknown as Record<string, unknown>)[entry.field]
}

// ── RBA key lookup (built from shared constants) ─────────────────────

const RBA_KEY_MAP = Object.fromEntries(
  RBA_KEY_LIST.map((r) => [
    r.key,
    { value: r.value, justification: r.justification },
  ])
) as Record<string, { value: string; justification: string }>

// ── Coverage column lookup (built from shared constants) ─────────────

const COVERAGE_COL_MAP = Object.fromEntries(
  COVERAGE_COLUMNS.map((c) => [c.dataIndex, c.field])
) as Record<string, string>

// ── Overview column lookup (camelCase dataIndex → snake_case zone field)

const OVERVIEW_COL_MAP = OVERVIEW_COL_TO_ZONE_FIELD

// ── Zone detail snapshot (for section-level zone_details anchor) ─────

function pickZoneDetails(
  zone: ZoneParams
): Record<string, unknown> | undefined {
  const details: Record<string, unknown> = {}
  let hasAny = false
  for (const entry of ZONE_DETAIL_FIELDS) {
    const v = resolveDetailLabel(zone, entry.label)
    if (v !== undefined) {
      details[entry.label] = v
      hasAny = true
    }
  }
  return hasAny ? details : undefined
}

// ── Synthetic zone-property resolver ─────────────────────────────────

function resolveSyntheticZoneProperty(
  zone: ZoneParams,
  property: string
): unknown | undefined {
  const segments = property.split('.')

  // dm.{dmId}.{column}
  if (
    segments[0] === ZONE_SYNTHETIC_PREFIX.DAMAGE_MECHANISM &&
    segments.length === 3
  ) {
    const dm = zone.damage_mechanisms?.find((m) => m.id === segments[1])
    if (!dm) return undefined
    const col = segments[2]
    if (col === DAMAGE_MECHANISM_COLUMNS.RATE) {
      return dm.rate_value !== undefined
        ? { rate_value: dm.rate_value, rate_unit: dm.rate_unit }
        : { susceptibility: dm.susceptibility }
    }
    return (dm as unknown as Record<string, unknown>)[col]
  }

  // details.{displayLabel}
  if (segments[0] === ZONE_SYNTHETIC_PREFIX.DETAILS && segments.length >= 2) {
    const label = segments.slice(1).join('.')
    return resolveDetailLabel(zone, label)
  }

  // strategy.{key}.{column}
  if (segments[0] === ZONE_SYNTHETIC_PREFIX.STRATEGY && segments.length === 3) {
    const strategy = zone.nii_strategy
    if (!strategy) return undefined
    const key = segments[1]
    const col = segments[2]
    if (col === 'justification') {
      return (
        getByPath(strategy, `${key}_justification`) ?? strategy.justification
      )
    }
    return getByPath(strategy, key)
  }

  // rba.{shortKey}.{column}
  if (segments[0] === ZONE_SYNTHETIC_PREFIX.RBA && segments.length === 3) {
    const rba = zone.rba_info
    if (!rba) return undefined
    const mapping = RBA_KEY_MAP[segments[1]]
    if (!mapping) return undefined
    const field =
      segments[2] === 'justification' ? mapping.justification : mapping.value
    return (rba as unknown as Record<string, unknown>)[field]
  }

  // coverage.{column}
  if (segments[0] === ZONE_SYNTHETIC_PREFIX.COVERAGE && segments.length === 2) {
    const calc = zone.coverage_calculation
    if (!calc) return undefined
    const field = COVERAGE_COL_MAP[segments[1]]
    if (!field) return undefined
    return (calc as unknown as Record<string, unknown>)[field]
  }

  // Overview table camelCase columns → real zone fields
  const overviewField = OVERVIEW_COL_MAP[property]
  if (overviewField) {
    if (overviewField === 'damage_mechanisms') {
      return (
        zone.damage_mechanisms?.map((dm) => dm.name).join(', ') || undefined
      )
    }
    const calc = zone.coverage_calculation
    if (calc) {
      const val = (calc as unknown as Record<string, unknown>)[overviewField]
      if (val !== undefined) return val
    }
    return (zone as unknown as Record<string, unknown>)[overviewField]
  }

  // Section-level mappings
  switch (property) {
    case ZONE_SECTION_KEYS.DAMAGE_MECHANISMS:
      return zone.damage_mechanisms
    case ZONE_SECTION_KEYS.ZONE_DETAILS:
      return pickZoneDetails(zone)
    case ZONE_SECTION_KEYS.COVERAGE_REQUIREMENTS:
    case ZONE_SECTION_KEYS.COVERAGE_BREAKDOWN:
      return zone.coverage_calculation
    default:
      return undefined
  }
}

// ── Strategy override alias ──────────────────────────────────────────

const STRATEGY_PATH_ALIASES: Record<string, string> = {
  'nii_strategy.strategy_override': 'nii_strategy.strategy_type_override',
}

// ── Core resolver (field, cell) ──────────────────────────────────────

/**
 * Pick the `FieldValues` slice the anchor points into: the per-component slice
 * when the anchor carries a `componentId`, else the top-level ticket slice.
 * Returns `undefined` when the anchor targets a component that hasn't been
 * materialized in `fieldValues.components` yet.
 */
function scopedSlice(
  anchor: { componentId?: string },
  fieldValues: FieldValues
): FieldValues | undefined {
  if (anchor.componentId) {
    return getComponentSubtree(fieldValues)?.[anchor.componentId]
  }
  return fieldValues
}

const coreValueResolver: AnchorValueResolver = (anchor, ctx) => {
  switch (anchor.type) {
    case 'field': {
      const slice = scopedSlice(anchor, ctx.fieldValues)
      if (!slice) return undefined
      return unwrapFieldValue(slice[anchor.fieldId])
    }

    case 'cell': {
      const slice = scopedSlice(anchor, ctx.fieldValues)
      if (!slice) return undefined
      const rows = unwrapFieldValue<Array<Record<string, unknown>>>(
        slice[anchor.fieldId]
      )
      if (!Array.isArray(rows)) return undefined
      const row = rows.find((r) => r._id === anchor.rowId)
      return row?.[anchor.columnKey]
    }

    default:
      return undefined
  }
}

// ── Zone resolver (zone-property, damage-mechanism, zone, cof) ──────

function findZone(
  zones: ZoneParams[] | undefined,
  zoneId: string
): ZoneParams | undefined {
  return zones?.find((z) => z.id === zoneId)
}

const zoneValueResolver: AnchorValueResolver = (anchor, ctx) => {
  switch (anchor.type) {
    case 'zone-property': {
      const zone = findZone(ctx.zones, anchor.zoneId)
      if (!zone) return undefined
      const synthetic = resolveSyntheticZoneProperty(zone, anchor.property)
      if (synthetic !== undefined) return synthetic
      const resolvedPath =
        STRATEGY_PATH_ALIASES[anchor.property] ?? anchor.property
      return getByPath(zone as unknown as Record<string, unknown>, resolvedPath)
    }

    case 'damage-mechanism': {
      const zone = findZone(ctx.zones, anchor.zoneId)
      if (!zone) return undefined
      const dm = zone.damage_mechanisms?.find(
        (m) => m.id === anchor.damageMechanismId
      )
      if (!dm) return undefined
      return {
        name: dm.name,
        rate_unit: dm.rate_unit,
        rate_value: dm.rate_value,
        susceptibility: dm.susceptibility,
        justification: dm.justification,
      }
    }

    case 'zone': {
      const zone = findZone(ctx.zones, anchor.zoneId)
      if (!zone) return undefined
      return {
        title: zone.title,
        description: zone.description,
        material: zone.material,
        geometry: zone.geometry,
      }
    }

    case 'cof': {
      return unwrapFieldValue(ctx.fieldValues['consequence_of_failure'])
    }

    default:
      return undefined
  }
}

// ── Public API ───────────────────────────────────────────────────────

const resolvers: AnchorValueResolver[] = [coreValueResolver, zoneValueResolver]

export function resolveCurrentValue(
  anchor: CommentAnchor,
  context: AnchorValueContext
): unknown | undefined {
  for (const resolver of resolvers) {
    const value = resolver(anchor, context)
    if (value !== undefined) return value
  }
  return undefined
}
